import { useUserSector } from "@/core/hooks/useUserSector";
import { usePermission } from "@/core/auth/usePermission";
import { useSectorWhatsAppHealth } from "@/features/whatsapp-status/hooks/useSectorWhatsAppHealth";
import {
  deriveCampaignHealthGate,
  type CampaignHealthGateView,
  type SectorConnectionType,
} from "@/features/campaigns/utils/campaignHealth";

export type CampaignDispatchGate = CampaignHealthGateView & {
  /** Ainda resolvendo setor/saúde — o consumidor mostra skeleton, não bloqueio. */
  isLoading: boolean;
  /** Deep-link para a tela de conexão do setor (resolve o motivo do bloqueio). */
  resolveConnectionPath: string;
  /**
   * Pode abrir a tela de conexão (`ui:settings:view` no setor ativo). Sem ela
   * a tela é bloqueada (SA-1360): o consumidor não mostra o CTA e completa a
   * copy com `ASK_ADMIN_TO_RESOLVE`, como o banner global de saúde.
   */
  canResolveConnection: boolean;
};

/**
 * Prontidão de disparo do setor ATIVO, compartilhada pelo builder (bloqueio
 * full-screen), pela lista (banner + lock do CTA) e por onde mais precisar
 * decidir "pode disparar agora?". Combina o tipo de conexão do setor (fonte
 * autoritativa, cobre domestic legado sem depender de snapshot) com o snapshot
 * de saúde da Meta — a mesma query cacheada do banner global (MainLayout), então
 * conexão saudável não paga custo novo.
 */
export function useCampaignDispatchGate(): CampaignDispatchGate {
  const { activeSectorId, sectorQuery } = useUserSector();
  const health = useSectorWhatsAppHealth(activeSectorId);
  const canResolveConnection = usePermission("ui:settings:view");

  const sectorType = sectorQuery.data?.session_connection_type as
    | SectorConnectionType
    | undefined;

  const view = deriveCampaignHealthGate(health.data, sectorType);

  return {
    ...view,
    isLoading: sectorQuery.isLoading || health.isLoading,
    resolveConnectionPath: `/settings/whatsapp?sector=${activeSectorId ?? ""}`,
    canResolveConnection,
  };
}
